import type { ReactNode } from 'react';

const controlClass =
    'w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200';
const textareaClass = `${controlClass} font-mono`;

export function LabeledCheckboxRow({
    label,
    checked,
    onCheckedChange,
}: {
    label: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
}) {
    return (
        <div className="flex items-center gap-2">
            <input
                type="checkbox"
                checked={checked}
                onChange={e => onCheckedChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <label className="text-xs text-gray-700 dark:text-gray-300">{label}</label>
        </div>
    );
}

export function LabeledSelectRow({
    label,
    description,
    value,
    options,
    onValueChange,
}: {
    label: string;
    description?: string;
    value: string;
    options: readonly string[];
    onValueChange: (value: string) => void;
}) {
    return (
        <div className="space-y-1">
            <label className="block text-xs text-gray-700 dark:text-gray-300">{label}</label>
            {description ? <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p> : null}
            <select
                className={controlClass}
                value={value}
                onChange={e => onValueChange(e.target.value)}
            >
                {options.map(opt => (
                    <option
                        key={opt}
                        value={opt}
                    >
                        {opt}
                    </option>
                ))}
            </select>
        </div>
    );
}

export function LabeledNumberRow({
    label,
    description,
    value,
    placeholder,
    onIntChange,
}: {
    label: string;
    description?: string;
    value: number;
    placeholder?: string;
    onIntChange: (value: number) => void;
}) {
    return (
        <div className="space-y-1">
            <label className="block text-xs text-gray-700 dark:text-gray-300">{label}</label>
            {description ? <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p> : null}
            <input
                type="number"
                value={Number.isNaN(value) ? '' : String(value)}
                placeholder={placeholder}
                onChange={e => {
                    const raw = e.target.value;
                    const n = raw === '' ? 0 : Number.parseInt(raw, 10);
                    onIntChange(Number.isNaN(n) ? 0 : n);
                }}
                className={controlClass}
            />
        </div>
    );
}

export function LabeledTextRow({
    label,
    description,
    value,
    placeholder,
    rows,
    monospace,
    labelAccessory,
    onValueChange,
}: {
    label: string;
    description?: string;
    value: string;
    placeholder?: string;
    rows: number;
    monospace?: boolean;
    labelAccessory?: ReactNode;
    onValueChange: (value: string) => void;
}) {
    return (
        <div className="space-y-1">
            <div className="flex items-center gap-1">
                <label className="text-xs text-gray-700 dark:text-gray-300">{label}</label>
                {labelAccessory}
            </div>
            <textarea
                rows={rows}
                value={value}
                placeholder={placeholder}
                onChange={e => onValueChange(e.target.value)}
                className={monospace ? textareaClass : controlClass}
            />
            {description ? <div className="text-xs text-gray-500 dark:text-gray-400">{description}</div> : null}
        </div>
    );
}
